import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchBar = ({ variant = "hero" }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchCities = async () => {
      if (query.length < 2) {
        setResults([]);
        setIsOpen(false);
        setNoResults(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(response.data);
        setNoResults(response.data.length === 0);
        setIsOpen(true);
      } catch (error) {
        console.error("Error searching cities:", error);
        setResults([]);
        setNoResults(true);
        setIsOpen(true);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchCities, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelectCity = (city) => {
    setQuery("");
    setIsOpen(false);
    setNoResults(false);
    navigate(`/ciudad/${city.id}`);
  };

  const isHero = variant === "hero";

  return (
    <div className="search-container" data-testid="search-bar">
      <div className="relative">
        <Search className={`absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 ${isHero ? 'text-gray-400' : 'text-muted-foreground'}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busca un intercambio en tu ciudad..."
          className={`search-input pl-14 pr-6 ${isHero ? 'text-lg' : 'text-base'}`}
          aria-label="Buscar ciudad para intercambio de idiomas"
          data-testid="search-input"
        />
        {loading && (
          <div className="absolute right-5 top-1/2 -translate-y-1/2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="search-dropdown"
          data-testid="search-results"
        >
          {results.length > 0 ? (
            results.map((city) => (
              <div
                key={city.id}
                className="search-result-item"
                onClick={() => handleSelectCity(city)}
                data-testid={`search-result-${city.id}`}
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={city.image} 
                    alt={city.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {city.name}
                  </p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {city.country}
                  </p>
                </div>
                <span className="text-2xl">{city.flag}</span>
              </div>
            ))
          ) : noResults && (
            <a
              href="https://www.languageexchanges.com"
              className="search-result-item hover:bg-primary/5"
              data-testid="no-results-lingua2"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ExternalLink className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">
                  Ciudad no disponible. Visita Lingua2
                </p>
                <p className="text-sm text-primary">
                  www.languageexchanges.com
                </p>
              </div>
            </a>
          )}
        </div>
      )}
    </div>
  );
};
